import React, { useState, useEffect, useCallback } from 'react';
import { adminLogin, adminOrders, adminInventory, updateInventory, shipOrder, adminPromos, createPromo, deactivatePromo, getReviews, adminAddReview, adminDeleteReview, adminSubscribers, getAnalytics, getReminderStats, getCaptureStats, exportCsvUrl, adminEmails, resendAdminEmail } from '../../api';
import { Loader2, Package, ClipboardList, LogOut, Tag, Star, Trash2, BarChart3, Mail } from 'lucide-react';
import { InventoryPanel } from '../InventoryPanel';
import { EmailLogPanel } from '../EmailLogPanel';
import { money } from '../../brand';

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem('wearhouse60_admin') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedKey, setSavedKey] = useState('');
  const [orderFilter, setOrderFilter] = useState('paid');
  const [trackingInputs, setTrackingInputs] = useState({});
  const [carrierInputs, setCarrierInputs] = useState({});
  const [shippingId, setShippingId] = useState('');
  const [promos, setPromos] = useState([]);
  const [reminderStats, setReminderStats] = useState(null);
  const [captureStats, setCaptureStats] = useState(null);
  const [promoForm, setPromoForm] = useState({ code: '', kind: 'percent', value: '', max: '' });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [resendingEmailId, setResendingEmailId] = useState('');

  const loadEmails = useCallback(async (tk) => {
    setEmailsLoading(true);
    try {
      const data = await adminEmails(tk);
      setEmails(data.emails || []);
    } catch (e) {
      setError('Impossible de charger le journal des emails.');
    } finally {
      setEmailsLoading(false);
    }
  }, []);

  const resendEmail = async (id) => {
    setError('');
    setResendingEmailId(id);
    try {
      await resendAdminEmail(token, id);
      await loadEmails(token);
    } catch (e) {
      setError(e?.response?.data?.detail || "L'email n'a pas pu être relancé.");
    } finally {
      setResendingEmailId('');
    }
  };

  const loadPromos = useCallback(async (tk) => {
    try { const d = await adminPromos(tk); setPromos(d.promos || []); } catch (e) { /* ignore */ }
    try { setReminderStats(await getReminderStats(tk)); } catch (e) { /* ignore */ }
    try { setCaptureStats(await getCaptureStats(tk)); } catch (e) { /* ignore */ }
  }, []);

  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const loadAnalytics = useCallback(async (tk) => {
    try { setAnalytics(await getAnalytics(tk)); } catch (e) { /* ignore */ }
  }, []);
  const [subCount, setSubCount] = useState(0);
  const [reviewForm, setReviewForm] = useState({ author: '', rating: 5, text: '' });
  const loadReviews = useCallback(async (tk) => {
    try { const d = await getReviews(); setReviews(d.reviews || []); } catch (e) { /* ignore */ }
    try { const s = await adminSubscribers(tk); setSubCount(s.count || 0); } catch (e) { /* ignore */ }
  }, []);
  const submitReview = async (e) => {
    e.preventDefault();
    const author = reviewForm.author.trim(); const text = reviewForm.text.trim();
    if (!author || !text) { setError('Auteur et avis requis.'); return; }
    setError('');
    try { await adminAddReview(token, { author, rating: Number(reviewForm.rating), text }); setReviewForm({ author: '', rating: 5, text: '' }); await loadReviews(token); }
    catch (e2) { setError("L'avis n'a pas pu être ajouté."); }
  };
  const removeReview = async (id) => {
    try { await adminDeleteReview(token, id); await loadReviews(token); } catch (e) { setError('Suppression impossible.'); }
  };

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const [o, inv] = await Promise.all([adminOrders(tk), adminInventory(tk)]);
      setOrders(o.orders || []);
      setInventory(inv.inventory || []);
    } catch (e) {
      setError('Impossible de charger les données de la boutique.');
      if (e?.response?.status === 401) {
        localStorage.removeItem('wearhouse60_admin');
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  useEffect(() => {
    if (token && tab === 'promos') loadPromos(token);
  }, [token, tab, loadPromos]);

  useEffect(() => {
    if (token && tab === 'reviews') loadReviews(token);
  }, [token, tab, loadReviews]);

  useEffect(() => {
    if (token && tab === 'analytics') loadAnalytics(token);
  }, [token, tab, loadAnalytics]);

  useEffect(() => {
    if (token && tab === 'emails') loadEmails(token);
  }, [token, tab, loadEmails]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { token: tk } = await adminLogin(password);
      localStorage.setItem('wearhouse60_admin', tk);
      setToken(tk);
    } catch (err) {
      setError('Connexion impossible. Vérifie ton mot de passe.');
    }
  };

  const logout = () => {
    localStorage.removeItem('wearhouse60_admin');
    setToken('');
  };

  const changeQty = async (row, qty) => {
    const q = Math.max(0, parseInt(qty || '0', 10));
    setError('');
    try {
      await updateInventory(token, {
        product_id: row.product_id,
        variant_id: row.variant_id,
        size: row.size,
        qty: q,
      });
      setInventory((prev) => prev.map((r) => r.product_id === row.product_id && r.variant_id === row.variant_id && r.size === row.size ? { ...r, qty: q } : r));
      const k = `${row.product_id}:${row.variant_id}:${row.size}`;
      setSavedKey(k);
      setTimeout(() => setSavedKey(''), 1200);
      return true;
    } catch (e) { setError('Le stock n’a pas été enregistré. Réessaie.'); return false; }
  };

  const shipOne = async (order) => {
    const tn = (trackingInputs[order.order_id] || '').trim();
    if (!tn) return;
    const carrier = (carrierInputs[order.order_id] || '').trim() || null;
    setError('');
    setShippingId(order.order_id);
    try {
      await shipOrder(token, order.order_id, { tracking_number: tn, carrier });
      setOrders((prev) => prev.map((x) => (x.order_id === order.order_id ? { ...x, status: 'shipped', tracking_number: tn, carrier } : x)));
      setTrackingInputs((p) => ({ ...p, [order.order_id]: '' }));
      setCarrierInputs((p) => ({ ...p, [order.order_id]: '' }));
    } catch (e) {
      setError("L'expédition n'a pas pu être enregistrée.");
    } finally {
      setShippingId('');
    }
  };

  const submitPromo = async (e) => {
    e.preventDefault();
    setError('');
    const code = promoForm.code.trim();
    const val = parseFloat(promoForm.value);
    if (!code || !val || val <= 0) { setError('Code et valeur requis.'); return; }
    const body = { code };
    if (promoForm.kind === 'percent') body.percent_off = val; else body.amount_off = val;
    if (promoForm.max) body.max_redemptions = parseInt(promoForm.max, 10);
    setCreatingPromo(true);
    try {
      await createPromo(token, body);
      setPromoForm({ code: '', kind: 'percent', value: '', max: '' });
      await loadPromos(token);
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Création du code impossible.');
    } finally {
      setCreatingPromo(false);
    }
  };

  const removePromo = async (id) => {
    setError('');
    try { await deactivatePromo(token, id); await loadPromos(token); }
    catch (e) { setError('Désactivation impossible.'); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f4f4] px-5">
        <form onSubmit={handleLogin} className="bg-white w-full max-w-sm p-10">
          <h1 className="font-display text-3xl uppercase text-center" data-testid="admin-title">WEARHOUSE 60</h1>
          <p className="text-black/50 text-sm text-center mt-1">Administration de la boutique</p>
          <input
            type="password"
            data-testid="admin-password"
            autoComplete="current-password"
            aria-label="Mot de passe administrateur"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="w-full border border-black/20 h-12 px-4 mt-6 outline-none focus:border-black font-cond tracking-wide"
          />
          {error && <p role="alert" data-testid="admin-login-error" className="text-red-800 text-sm mt-2">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login"
            className="w-full bg-black text-white h-12 mt-4 font-cond uppercase text-sm hover:bg-[#2b3a26] transition-colors"
          >
            Connexion
          </button>
          <a href="/" data-testid="admin-login-back" className="block text-xs text-center mt-5 underline">Retour à la boutique</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f4]">
      <header className="bg-black text-white">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <a href="/" data-testid="admin-home" className="font-display text-lg sm:text-2xl">WEARHOUSE 60</a>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 font-cond uppercase text-xs hover:text-[#a9b69b]">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {error && <p role="alert" data-testid="admin-save-error" className="error-message mb-5">{error}</p>}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'orders', label: 'Commandes', icon: ClipboardList },
            { id: 'inventory', label: 'Stock', icon: Package },
            { id: 'promos', label: 'Codes promo', icon: Tag },
            { id: 'reviews', label: 'Avis', icon: Star },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'emails', label: 'Emails', icon: Mail },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 h-11 font-cond uppercase tracking-wide text-sm transition-colors ${
                tab === t.id ? 'bg-black text-white' : 'bg-white text-black hover:bg-black/5'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-black/50 py-10">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
          </div>
        )}

        {!loading && tab === 'orders' && (() => {
          const paidOrders = orders.filter((o) => o.payment_status === 'paid');
          const orderTotal = (o) => (o.amount_total != null ? o.amount_total : o.amount) || 0;
          const totalVentes = paidOrders.reduce((sum, o) => sum + orderTotal(o), 0);
          const toShip = paidOrders.filter((o) => o.status !== 'shipped');
          const shippedOrders = orders.filter((o) => o.status === 'shipped');
          const shown = orderFilter === 'paid' ? paidOrders
            : orderFilter === 'to_ship' ? toShip
            : orderFilter === 'shipped' ? shippedOrders
            : orders;
          return (
            <>
              <div className="bg-white mb-4 flex flex-wrap items-center justify-between gap-4 p-5 border-l-4 border-[#2b3a26]">
                <div>
                  <p className="font-cond uppercase tracking-wide text-xs text-black/50">Total des ventes (payées)</p>
                  <p className="font-display text-3xl" data-testid="admin-total-sales">{money(totalVentes / 100)}</p>
                </div>
                <p className="font-cond uppercase text-sm text-black/60" data-testid="admin-paid-count">
                  {paidOrders.length} commande{paidOrders.length > 1 ? 's' : ''} payée{paidOrders.length > 1 ? 's' : ''}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { id: 'to_ship', label: `À expédier${toShip.length ? ` (${toShip.length})` : ''}` },
                  { id: 'shipped', label: `Expédiées${shippedOrders.length ? ` (${shippedOrders.length})` : ''}` },
                  { id: 'paid', label: 'Payées' },
                  { id: 'all', label: 'Toutes' },
                ].map((f) => (
                  <button
                    key={f.id}
                    data-testid={`admin-order-filter-${f.id}`}
                    onClick={() => setOrderFilter(f.id)}
                    className={`px-4 h-9 font-cond uppercase tracking-wide text-xs transition-colors ${
                      orderFilter === f.id ? 'bg-[#2b3a26] text-white' : 'bg-white text-black hover:bg-black/5'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="bg-white">
                {shown.length === 0 ? (
                  <p data-testid="admin-orders-empty" className="p-8 text-black/50 text-center">
                    {({
                      paid: 'Aucune commande payée pour le moment.',
                      to_ship: 'Rien à expédier — tout est à jour.',
                      shipped: 'Aucune commande expédiée pour le moment.',
                      all: 'Aucune commande pour le moment.',
                    })[orderFilter]}
                  </p>
                ) : (
                  <div className="divide-y">
                    {shown.map((o) => (
                      <div key={o.order_id} className="p-5" data-testid={`admin-order-${o.order_id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <span className="font-cond uppercase tracking-wide text-sm">#{o.order_id.slice(0, 8)}</span>
                            <span
                              data-testid={`admin-order-status-${o.order_id}`}
                              className={`ml-3 text-[11px] font-semibold px-2 py-0.5 uppercase ${
                                o.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-700'
                                  : o.payment_status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-black/10 text-black/50'
                              }`}
                            >
                              {({ paid: 'Payée', pending: 'En attente', failed: 'Échouée', expired: 'Expirée' })[o.payment_status] || o.payment_status}
                            </span>
                          </div>
                          <span data-testid={`admin-order-total-${o.order_id}`} className="font-display text-lg">{money(orderTotal(o) / 100)}</span>
                        </div>
                        {o.shipping_amount ? (
                          <p className="text-[11px] text-black/40 mt-0.5 text-right">dont livraison {money(o.shipping_amount / 100)}</p>
                        ) : null}
                        <p className="text-xs text-black/50 mt-1 break-all">
                          {new Date(o.created_at).toLocaleString()} {o.email ? `· ${o.email}` : ''}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {o.items.map((it, i) => (
                            <span key={i} className="text-xs bg-[#f4f4f4] px-2 py-1 font-cond uppercase tracking-wide">
                              {it.name} · {it.size} ×{it.quantity}
                            </span>
                          ))}
                        </div>
                        {o.payment_status === 'paid' && (
                          o.status === 'shipped' ? (
                            <p className="mt-3 text-xs flex items-center gap-2 flex-wrap" data-testid={`admin-order-shipped-${o.order_id}`}>
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 uppercase font-semibold">Expédiée</span>
                              {o.carrier ? <span className="text-black/70 font-semibold">{o.carrier}</span> : null}
                              {o.tracking_number ? <span className="text-black/60">Suivi : {o.tracking_number}</span> : null}
                            </p>
                          ) : (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <input
                                value={trackingInputs[o.order_id] || ''}
                                onChange={(e) => setTrackingInputs((p) => ({ ...p, [o.order_id]: e.target.value }))}
                                placeholder="N° de suivi"
                                data-testid={`ship-input-${o.order_id}`}
                                className="h-9 border border-black/20 px-3 text-sm outline-none focus:border-black"
                              />
                              <select
                                value={carrierInputs[o.order_id] || ''}
                                onChange={(e) => setCarrierInputs((p) => ({ ...p, [o.order_id]: e.target.value }))}
                                data-testid={`ship-carrier-${o.order_id}`}
                                className="h-9 border border-black/20 px-2 text-sm outline-none focus:border-black bg-white"
                              >
                                <option value="">Transporteur…</option>
                                <option value="Colissimo">Colissimo</option>
                                <option value="Chronopost">Chronopost</option>
                                <option value="Mondial Relay">Mondial Relay</option>
                                <option value="La Poste">La Poste</option>
                                <option value="UPS">UPS</option>
                                <option value="DHL">DHL</option>
                                <option value="FedEx">FedEx</option>
                                <option value="Autre">Autre</option>
                              </select>
                              <button
                                onClick={() => shipOne(o)}
                                disabled={shippingId === o.order_id || !(trackingInputs[o.order_id] || '').trim()}
                                data-testid={`ship-button-${o.order_id}`}
                                className="h-9 px-4 bg-[#2b3a26] text-white font-cond uppercase text-xs hover:bg-black transition-colors disabled:opacity-40"
                              >
                                {shippingId === o.order_id ? 'Envoi…' : 'Expédier + email'}
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {!loading && tab === 'inventory' && <InventoryPanel rows={inventory} onSave={changeQty} savedKey={savedKey} />}

        {!loading && tab === 'emails' && (
          <EmailLogPanel emails={emails} loading={emailsLoading} resendingId={resendingEmailId} onResend={resendEmail} />
        )}

        {!loading && tab === 'analytics' && (
          <div className="space-y-5" data-testid="analytics-panel">
            {!analytics ? <p className="p-8 text-black/50 text-center">Chargement…</p> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { l: 'Vues (total)', v: analytics.views_total, t: 'analytics-views' },
                    { l: "Vues aujourd'hui", v: analytics.views_today },
                    { l: 'Commandes payées', v: analytics.orders_paid },
                    { l: 'Chiffre d\'affaires', v: money(analytics.revenue), t: 'analytics-revenue' },
                    { l: 'Taux de conversion', v: `${analytics.conversion} %`, t: 'analytics-conversion' },
                    { l: 'Paniers abandonnés', v: analytics.abandoned },
                    { l: 'Abonnés', v: analytics.subscribers },
                  ].map((c, i) => (
                    <div key={i} className="bg-white p-4 border-l-4 border-[#2b3a26]" data-testid={c.t}>
                      <p className="font-cond uppercase text-[11px] text-black/50">{c.l}</p>
                      <p className="font-display text-2xl">{c.v}</p>
                    </div>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-white p-4">
                    <p className="font-cond uppercase text-xs text-black/50 mb-2">Stock par taille</p>
                    <div className="flex gap-4">{Object.entries(analytics.stock).map(([s, q]) => (
                      <span key={s} className="font-cond">T{s} : <strong>{q}</strong></span>
                    ))}</div>
                  </div>
                  <div className="bg-white p-4">
                    <p className="font-cond uppercase text-xs text-black/50 mb-2">Top sources</p>
                    {analytics.top_sources.length === 0 ? <span className="text-black/40 text-sm">—</span> : (
                      <ul className="text-sm">{analytics.top_sources.map((s) => (
                        <li key={s.source} className="flex justify-between border-b border-black/5 py-1"><span>{s.source}</span><strong>{s.views}</strong></li>
                      ))}</ul>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a href={exportCsvUrl('orders', token)} data-testid="export-orders" className="h-10 px-5 inline-flex items-center bg-[#2b3a26] text-white font-cond uppercase text-sm hover:bg-black">Exporter commandes (CSV)</a>
                  <a href={exportCsvUrl('subscribers', token)} data-testid="export-subscribers" className="h-10 px-5 inline-flex items-center bg-black/80 text-white font-cond uppercase text-sm hover:bg-black">Exporter abonnés (CSV)</a>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && tab === 'reviews' && (
          <div className="space-y-6">
            <div className="bg-white p-4 border-l-4 border-[#2b3a26] flex items-center justify-between">
              <span className="font-cond uppercase text-xs text-black/50">Abonnés newsletter</span>
              <strong className="font-display text-2xl" data-testid="admin-subscriber-count">{subCount}</strong>
            </div>
            <form onSubmit={submitReview} className="bg-white p-5 space-y-3" data-testid="review-form">
              <div className="flex flex-wrap gap-3">
                <input value={reviewForm.author} onChange={(e) => setReviewForm((p) => ({ ...p, author: e.target.value }))} placeholder="Nom du client" data-testid="review-author" className="h-10 border border-black/20 px-3 text-sm outline-none focus:border-black flex-1 min-w-[160px]" />
                <select value={reviewForm.rating} onChange={(e) => setReviewForm((p) => ({ ...p, rating: e.target.value }))} data-testid="review-rating" className="h-10 border border-black/20 px-2 bg-white">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
                </select>
              </div>
              <textarea value={reviewForm.text} onChange={(e) => setReviewForm((p) => ({ ...p, text: e.target.value }))} placeholder="L'avis du client…" data-testid="review-text" rows={3} className="w-full border border-black/20 p-3 text-sm outline-none focus:border-black" />
              <button type="submit" data-testid="review-create" className="h-10 px-5 bg-[#2b3a26] text-white font-cond uppercase text-sm hover:bg-black transition-colors">Publier l&apos;avis</button>
            </form>
            <div className="bg-white">
              {reviews.length === 0 ? (
                <p data-testid="reviews-empty" className="p-8 text-black/50 text-center">Aucun avis publié. Ajoute le premier ci-dessus.</p>
              ) : (
                <div className="divide-y">
                  {reviews.map((r) => (
                    <div key={r.id} className="p-4 flex items-start justify-between gap-3" data-testid={`review-row-${r.id}`}>
                      <div>
                        <p className="font-cond uppercase text-sm">{r.author} · {'★'.repeat(r.rating)}<span className="text-black/20">{'★'.repeat(5 - r.rating)}</span></p>
                        <p className="text-sm text-black/70 mt-1">{r.text}</p>
                      </div>
                      <button onClick={() => removeReview(r.id)} data-testid={`review-delete-${r.id}`} aria-label="Supprimer l'avis" className="text-red-700 hover:text-red-900 shrink-0"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && tab === 'promos' && (
          <div className="space-y-6">
            {captureStats && (
              <div className="bg-white p-5 space-y-4" data-testid="capture-stats">
                <h3 className="font-cond uppercase text-sm tracking-wide">Capture d'emails — pop-up & newsletter</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { l: 'Emails captés (pop-up)', v: captureStats.popup_captured, t: 'capture-popup' },
                    { l: 'Commandes issues du pop-up', v: captureStats.popup_converted, t: 'capture-popup-converted' },
                    { l: 'Taux de conversion pop-up', v: `${captureStats.popup_rate} %`, t: 'capture-popup-rate' },
                    { l: 'CA généré par le pop-up', v: money(captureStats.popup_revenue), t: 'capture-popup-revenue' },
                  ].map((k) => (
                    <div key={k.l} className="border border-black/10 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-black/50">{k.l}</p>
                      <p className="font-display text-xl mt-1" data-testid={k.t}>{k.v}</p>
                    </div>
                  ))}
                </div>
                {captureStats.sources.length === 0 ? (
                  <p className="text-sm text-black/50" data-testid="capture-empty">Aucun email capté pour le moment.</p>
                ) : (
                  <div className="divide-y border-t border-black/10" data-testid="capture-sources">
                    {captureStats.sources.map((s) => (
                      <div key={s.source} className="flex flex-wrap justify-between gap-2 py-2 text-sm" data-testid={`capture-source-${s.source}`}>
                        <span className="font-cond uppercase">{s.label}</span>
                        <span className="text-black/60">{s.captured} email{s.captured > 1 ? 's' : ''} · {s.codes} code{s.codes > 1 ? 's' : ''}</span>
                        <span className="text-black/60">{s.converted} commande{s.converted > 1 ? 's' : ''} ({s.rate} %)</span>
                        <strong>{money(s.revenue)}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {reminderStats && (
              <div className="bg-white p-5 space-y-4" data-testid="reminder-stats">
                <h3 className="font-cond uppercase text-sm tracking-wide">Relances panier abandonné — conversions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { l: '1re relance envoyée (-10 %)', v: reminderStats.reminders_sent, t: 'reminder-sent-1' },
                    { l: '2e relance envoyée (-17 %)', v: reminderStats.reminders2_sent, t: 'reminder-sent-2' },
                    { l: 'Paniers récupérés', v: `${reminderStats.converted} (${reminderStats.converted_1} + ${reminderStats.converted_2})`, t: 'reminder-converted' },
                    { l: 'CA récupéré', v: money(reminderStats.revenue_recovered), t: 'reminder-revenue' },
                    { l: 'Taux 1re relance', v: `${reminderStats.rate_1} %`, t: 'reminder-rate-1' },
                    { l: 'Taux 2e relance', v: `${reminderStats.rate_2} %`, t: 'reminder-rate-2' },
                    { l: 'Remises accordées', v: money(reminderStats.discount_given), t: 'reminder-discount' },
                  ].map((k) => (
                    <div key={k.l} className="border border-black/10 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-black/50">{k.l}</p>
                      <p className="font-display text-xl mt-1" data-testid={k.t}>{k.v}</p>
                    </div>
                  ))}
                </div>
                {reminderStats.orders.length > 0 && (
                  <div className="divide-y border-t border-black/10" data-testid="reminder-orders">
                    {reminderStats.orders.map((o) => (
                      <div key={o.order_id} className="flex flex-wrap justify-between gap-2 py-2 text-sm">
                        <span className="font-mono text-xs">{o.code}</span>
                        <span className="text-black/60">{o.email || '—'}</span>
                        <span className="text-black/60">-{money(o.discount)}</span>
                        <strong>{money(o.amount)}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <form onSubmit={submitPromo} className="bg-white p-5 flex flex-wrap items-end gap-3" data-testid="promo-form">
              <div className="flex flex-col">
                <span className="font-cond uppercase text-xs text-black/50 mb-1">Code</span>
                <input value={promoForm.code} onChange={(e) => setPromoForm((p) => ({ ...p, code: e.target.value }))} placeholder="ex. WELCOME10" data-testid="promo-code" className="h-10 border border-black/20 px-3 uppercase outline-none focus:border-black" />
              </div>
              <div className="flex flex-col">
                <span className="font-cond uppercase text-xs text-black/50 mb-1">Type</span>
                <select value={promoForm.kind} onChange={(e) => setPromoForm((p) => ({ ...p, kind: e.target.value }))} data-testid="promo-kind" className="h-10 border border-black/20 px-3 outline-none focus:border-black bg-white">
                  <option value="percent">% de remise</option>
                  <option value="amount">€ de remise</option>
                </select>
              </div>
              <div className="flex flex-col">
                <span className="font-cond uppercase text-xs text-black/50 mb-1">Valeur</span>
                <input type="number" min="1" step="0.01" value={promoForm.value} onChange={(e) => setPromoForm((p) => ({ ...p, value: e.target.value }))} placeholder={promoForm.kind === 'percent' ? 'ex. 10' : 'ex. 15'} data-testid="promo-value" className="h-10 w-28 border border-black/20 px-3 outline-none focus:border-black" />
              </div>
              <div className="flex flex-col">
                <span className="font-cond uppercase text-xs text-black/50 mb-1">Max util. (option)</span>
                <input type="number" min="1" step="1" value={promoForm.max} onChange={(e) => setPromoForm((p) => ({ ...p, max: e.target.value }))} placeholder="illimité" data-testid="promo-max" className="h-10 w-28 border border-black/20 px-3 outline-none focus:border-black" />
              </div>
              <button type="submit" disabled={creatingPromo} data-testid="promo-create" className="h-10 px-5 bg-[#2b3a26] text-white font-cond uppercase text-sm hover:bg-black transition-colors disabled:opacity-50">
                {creatingPromo ? 'Création…' : 'Créer le code'}
              </button>
            </form>

            <div className="bg-white">
              {promos.length === 0 ? (
                <p data-testid="promos-empty" className="p-8 text-black/50 text-center">Aucun code promo. Créez-en un ci-dessus.</p>
              ) : (
                <div className="divide-y">
                  {promos.map((p) => (
                    <div key={p.id} className="p-4 flex items-center justify-between gap-3" data-testid={`promo-row-${p.code}`}>
                      <div>
                        <span className="font-cond uppercase tracking-wide text-sm">{p.code}</span>
                        <span className="ml-3 text-sm text-black/70">{p.percent_off ? `${p.percent_off}%` : money((p.amount_off || 0) / 100)} de remise</span>
                        {!p.active && <span className="ml-2 text-[11px] bg-black/10 text-black/50 px-2 py-0.5 uppercase">Inactif</span>}
                        <p className="text-xs text-black/40 mt-1">{p.times_redeemed || 0} utilisation{(p.times_redeemed || 0) > 1 ? 's' : ''}{p.max_redemptions ? ` / ${p.max_redemptions}` : ''}</p>
                      </div>
                      {p.active && (
                        <button onClick={() => removePromo(p.id)} data-testid={`promo-deactivate-${p.code}`} className="text-xs text-red-700 underline whitespace-nowrap">Désactiver</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
